import type { NormalizedSpot } from '../components/RoomLayoutMap/spotKey';
import type { BookSpotReserveParams, InteractiveSpotBookingParamsLike } from './types';

/** Value for POST body `spot_id` (label preferred when API sends both UUID and name). */
export function getSpotPayloadForBookSpot(normalized: NormalizedSpot): string | null {
  return normalized.displayLabel ?? normalized.apiSpotId;
}

/** Drops a stale route-level `isChangeSpotFlow` so only the action-level flag decides the flow. */
export function omitIsChangeSpotFlow(
  params: InteractiveSpotBookingParamsLike
): Omit<InteractiveSpotBookingParamsLike, 'isChangeSpotFlow'> {
  const { isChangeSpotFlow: _isChangeSpotFlow, ...rest } = params;
  void _isChangeSpotFlow;
  return rest;
}

/**
 * Drops every pre-reserve member-spot route param: the explicit names (`previousSpotName`,
 * `currentBookedSpotName`, `memberBookedSpotName`), which outrank the arrays in
 * `getExplicitMemberSpotFromParams`, and the booked-spot arrays themselves. A reserve that replaces
 * the whole spot set has to clear all of them, otherwise the pre-reserve member spot still wins in
 * `getPreviousSpotIdFromParams` and lands on the Success screen. Stripping the arrays here rather
 * than relying on the caller's object literal overriding them keeps the guarantee independent of
 * field ordering.
 */
export function omitPreReserveMemberContext<T extends InteractiveSpotBookingParamsLike>(
  params: T
): Omit<
  T,
  | 'previousSpotName'
  | 'currentBookedSpotName'
  | 'memberBookedSpotName'
  | 'bookedSpots'
  | 'booked_spots'
  | 'bookedSpotNames'
  | 'booked_spot_names'
> {
  const {
    previousSpotName: _previousSpotName,
    currentBookedSpotName: _currentBookedSpotName,
    memberBookedSpotName: _memberBookedSpotName,
    bookedSpots: _bookedSpots,
    booked_spots: _bookedSpotsWire,
    bookedSpotNames: _bookedSpotNames,
    booked_spot_names: _bookedSpotNamesWire,
    ...rest
  } = params;
  void _previousSpotName;
  void _currentBookedSpotName;
  void _memberBookedSpotName;
  void _bookedSpots;
  void _bookedSpotsWire;
  void _bookedSpotNames;
  void _bookedSpotNamesWire;
  return rest;
}

/**
 * Drops the route-level navigation params (`action`, `type`). `buildBookSpotReserveMeta` sets both
 * for the flows that need them, so a stale route value must not ride along into the reserve meta and
 * reach the Success screen.
 */
export function omitReserveNavigationParams<T extends InteractiveSpotBookingParamsLike>(
  params: T
): Omit<T, 'action' | 'type'> {
  const { action: _action, type: _type, ...rest } = params;
  void _action;
  void _type;
  return rest;
}

export function buildBookParamsForReserve(
  params: InteractiveSpotBookingParamsLike,
  isChangeSpotFlow: boolean,
  resolvedSpotId: string,
  displayLabel: string | null | undefined
): BookSpotReserveParams {
  return {
    ...omitIsChangeSpotFlow(params),
    ...(isChangeSpotFlow ? { isChangeSpotFlow: true as const } : {}),
    bookSpotPayload: resolvedSpotId,
    ...(displayLabel != null ? { spotDisplayLabel: displayLabel } : {})
  };
}
